<template>
  <div class="login-email-start password-reset-phone-form">
    <p class="login-reset-intro">
      {{
        linkedAccountMode
          ? "Отправим код для сброса пароля в WhatsApp на номер, привязанный к аккаунту."
          : "Укажите номер WhatsApp — отправим код для сброса пароля"
      }}
    </p>
    <template v-if="linkedAccountMode">
      <p class="login-reset-linked-phone" aria-live="polite">
        <span class="login-reset-linked-phone__label">Номер для кода</span>
        <strong class="login-reset-linked-phone__value">{{ formattedDisplayPhone }}</strong>
      </p>
      <p v-if="phoneError" class="login-field__hint login-field__hint--error login-reset-linked-phone__error">{{ phoneError }}</p>
    </template>
    <label v-else class="login-field">
      <span>Номер WhatsApp</span>
      <div class="login-field__input-wrap login-field__input-wrap--phone">
        <img :src="whatsappOriginalIcon" alt="" class="login-field__leading-icon" width="20" height="20" aria-hidden="true" />
        <input
          :value="phone"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
          placeholder="+7 (999) 000-00-00"
          :aria-invalid="Boolean(phoneError)"
          :class="{ 'is-invalid': Boolean(phoneError) }"
          @blur="phoneTouched = true"
          @input="onPhoneInput"
        />
      </div>
      <p v-if="phoneError" class="login-field__hint login-field__hint--error">{{ phoneError }}</p>
    </label>
    <div
      v-if="!integratedOtpFlow && passwordResetFeedback !== 'idle'"
      class="login-reset-feedback"
      :class="{
        'login-reset-feedback--sending': passwordResetFeedback === 'sending',
        'login-reset-feedback--sent': passwordResetFeedback === 'sent',
      }"
      role="status"
      aria-live="polite"
    >
      <Icon
        :name="passwordResetFeedback === 'sending' ? 'loading-line' : 'check-circle'"
        :size="16"
        class="login-reset-feedback__icon"
        :class="{ 'is-spin': passwordResetFeedback === 'sending' }"
      />
      <span class="login-reset-feedback__text">{{ passwordResetFeedbackText }}</span>
    </div>
    <button
      class="login-email-start__continue"
      type="button"
      :class="{
        'login-email-start__continue--loading': integratedOtpFlow && passwordResetSending,
        'login-email-start__continue--success': integratedOtpFlow && integratedSendSuccess,
      }"
      :disabled="!canSubmitPasswordReset"
      :aria-label="passwordResetSendButtonAriaLabel"
      :aria-busy="integratedOtpFlow && passwordResetSending"
      @click="submitPasswordReset"
    >
      <span class="login-email-start__continue-content">
        <template v-if="integratedOtpFlow && passwordResetSending">
          <span class="login-email-start__electric" aria-hidden="true">
            <Icon name="loading-line" :size="16" class="is-spin login-email-start__electric-bolt login-email-start__electric-bolt--on-dark" />
          </span>
          <span aria-live="polite">{{ passwordResetSendButtonLabel }}</span>
        </template>
        <template v-else-if="integratedOtpFlow && integratedSendSuccess">
          <span class="login-email-start__electric" aria-hidden="true">
            <Icon name="check-circle" :size="16" class="login-email-start__electric-bolt login-email-start__electric-bolt--success-mark" />
          </span>
          <span aria-live="polite">{{ passwordResetSendButtonLabel }}</span>
        </template>
        <template v-else>
          <span class="login-email-start__electric" aria-hidden="true">
            <span class="login-email-start__electric-halo"></span>
            <span class="login-email-start__electric-spark login-email-start__electric-spark-a"></span>
            <span class="login-email-start__electric-spark login-email-start__electric-spark-b"></span>
            <span class="login-email-start__electric-spark login-email-start__electric-spark-c"></span>
            <span class="login-email-start__electric-spark login-email-start__electric-spark-d"></span>
            <Icon name="lightning" :size="16" class="login-email-start__electric-bolt" />
          </span>
          <span aria-live="polite">{{ passwordResetSendButtonLabel }}</span>
        </template>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import whatsappOriginalIcon from "@/assets/icons/New/Shape=Original, Colored=True.svg";
import { startPasswordReset } from "@/api/authApi";
import { AuthHttpError } from "@/api/geniBackend";
import Icon from "@/components/ui/Icon.vue";
import { captureEvent } from "@/lib/analytics";
import { formatPhoneInput, isPhoneComplete, resolvePhoneInputValue } from "@/lib/phoneAuth";
import { computed, onBeforeUnmount, ref } from "vue";

const phone = defineModel<string>("phone", { default: "" });

const emit = defineEmits<{
  (e: "codeSent", payload: { challengeId: string; resendAvailableInSeconds: number }): void;
}>();

const props = withDefaults(
  defineProps<{
    /** Блокирует кнопку отправки (напр. глобальная загрузка на экране входа). */
    locked?: boolean;
    /** Родитель ведёт на шаг OTP после отправки кода (задержка и emit; бэкенд подключат отдельно). */
    integratedOtpFlow?: boolean;
    /** Номер только из аккаунта (настройки): без поля ввода, отправка на привязанный телефон. */
    linkedAccountMode?: boolean;
    /** Точка входа во флоу сброса — попадает в PostHog как `surface`. */
    surface?: "login_screen" | "settings";
  }>(),
  { locked: false, integratedOtpFlow: false, linkedAccountMode: false, surface: "login_screen" },
);

type PasswordResetFeedback = "idle" | "sending" | "sent";

const passwordResetFeedback = ref<PasswordResetFeedback>("idle");
const phoneTouched = ref(false);
const submitAttempted = ref(false);
const serverPhoneError = ref("");

/** Интервал между повторными отправками кода (секунды; при появлении API брать с ответа сервера). */
const PASSWORD_RESET_RESEND_COOLDOWN_SECONDS = 60;
/** Длительность loading на кнопке до показа успеха (пока нет ответа API). */
const PASSWORD_RESET_SEND_MIN_INDICATOR_MS = 480;
/** Пауза на success-состоянии кнопки перед переходом на OTP. */
const PASSWORD_RESET_INTEGRATED_SUCCESS_MS = 420;

let resendIntervalId: number | null = null;
const resendRemainingSeconds = ref(0);

const formattedDisplayPhone = computed(() => formatPhoneInput(phone.value) || phone.value.trim() || "—");

const clientPhoneError = computed(() => {
  if (props.linkedAccountMode) {
    if (!phone.value.trim()) return "В профиле не указан номер — добавьте телефон в блоке «Профиль» и сохраните.";
    if (!isPhoneComplete(phone.value)) return "Номер в профиле указан не полностью — исправьте его в блоке «Профиль».";
    return "";
  }
  if (!phoneTouched.value && !submitAttempted.value) return "";
  if (!phone.value.trim()) return "Введите номер WhatsApp.";
  if (!isPhoneComplete(phone.value)) return "Укажите корректный номер WhatsApp.";
  return "";
});

const phoneError = computed(() => clientPhoneError.value || serverPhoneError.value);

const passwordResetFeedbackText = computed(() => {
  if (passwordResetFeedback.value === "sending") return "Отправляем код";
  if (passwordResetFeedback.value === "sent") return "Код отправлен";
  return "";
});

const passwordResetSending = ref(false);
const integratedSendSuccess = ref(false);

const canSubmitPasswordReset = computed(() => {
  if (props.locked || passwordResetSending.value || integratedSendSuccess.value) return false;
  if (!isPhoneComplete(phone.value)) return false;
  if (clientPhoneError.value) return false;
  if (props.integratedOtpFlow) return true;
  return resendRemainingSeconds.value === 0 && passwordResetFeedback.value !== "sending";
});

const passwordResetSendButtonLabel = computed(() => {
  if (props.integratedOtpFlow && integratedSendSuccess.value) return "Код отправлен";
  if (passwordResetSending.value) return "Отправляем код...";
  if (!props.integratedOtpFlow && resendRemainingSeconds.value > 0) {
    return `Отправить новый код через ${resendRemainingSeconds.value} сек.`;
  }
  return "Отправить код на WhatsApp";
});

const passwordResetSendButtonAriaLabel = computed(() => {
  if (props.integratedOtpFlow && integratedSendSuccess.value) return "Код отправлен в WhatsApp";
  if (passwordResetSending.value) return "Отправляем код";
  if (!props.integratedOtpFlow && resendRemainingSeconds.value > 0) {
    return `Отправить новый код на WhatsApp можно через ${resendRemainingSeconds.value} сек.`;
  }
  return "Отправить код на WhatsApp";
});

function clearResendCountdown() {
  if (resendIntervalId !== null) {
    window.clearInterval(resendIntervalId);
    resendIntervalId = null;
  }
}

function startResendCountdown(seconds: number) {
  clearResendCountdown();
  resendRemainingSeconds.value = seconds;

  if (seconds <= 0) {
    return;
  }

  resendIntervalId = window.setInterval(() => {
    if (resendRemainingSeconds.value <= 1) {
      resendRemainingSeconds.value = 0;
      clearResendCountdown();
      return;
    }

    resendRemainingSeconds.value -= 1;
  }, 1000);
}

function onPhoneInput(event: Event) {
  const target = event.target as HTMLInputElement;
  phone.value = resolvePhoneInputValue(target.value, event);
  serverPhoneError.value = "";
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function submitPasswordReset() {
  phoneTouched.value = true;
  submitAttempted.value = true;
  if (clientPhoneError.value) {
    return;
  }

  if (props.integratedOtpFlow) {
    passwordResetSending.value = true;
    integratedSendSuccess.value = false;
    serverPhoneError.value = "";
    const sendStart = Date.now();
    try {
      const response = await startPasswordReset(phone.value);
      captureEvent("[CardGenius] Сброс пароля: запрос OTP", {
        surface: props.surface,
        outcome: "sent",
      });
      const elapsed = Date.now() - sendStart;
      if (elapsed < PASSWORD_RESET_SEND_MIN_INDICATOR_MS) {
        await delay(PASSWORD_RESET_SEND_MIN_INDICATOR_MS - elapsed);
      }
      passwordResetSending.value = false;
      integratedSendSuccess.value = true;
      await delay(PASSWORD_RESET_INTEGRATED_SUCCESS_MS);
      emit("codeSent", {
        challengeId: response.challengeId,
        resendAvailableInSeconds: response.resendAvailableInSeconds,
      });
    } catch (error) {
      captureEvent("[CardGenius] Сброс пароля: запрос OTP", {
        surface: props.surface,
        outcome:
          error instanceof AuthHttpError && error.status === 404
            ? "not_found_phone"
            : "error",
      });
      const message = error instanceof Error ? error.message : "Не удалось отправить код";
      serverPhoneError.value = message;
    } finally {
      passwordResetSending.value = false;
      integratedSendSuccess.value = false;
    }
    return;
  }

  passwordResetFeedback.value = "sending";

  // Локальная задержка для экранов без общего OTP (например настройки).
  await delay(PASSWORD_RESET_SEND_MIN_INDICATOR_MS);
  passwordResetFeedback.value = "sent";
  startResendCountdown(PASSWORD_RESET_RESEND_COOLDOWN_SECONDS);
}

function resetFeedback() {
  passwordResetFeedback.value = "idle";
  integratedSendSuccess.value = false;
  startResendCountdown(0);
}

defineExpose({ resetFeedback });

onBeforeUnmount(() => {
  clearResendCountdown();
});
</script>

<style scoped lang="scss">
.password-reset-phone-form {
  width: 100%;
  max-width: 100%;
}

.login-field {
  display: grid;
  gap: 6px;
  text-align: left;
}

.login-field span {
  font-size: 13px;
  font-weight: 500;
}

.login-reset-intro {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--muted-foreground);
}

.login-reset-linked-phone {
  margin: 0;
  display: grid;
  gap: 6px;
  text-align: left;
}

.login-reset-linked-phone__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--foreground);
}

.login-reset-linked-phone__value {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
  color: var(--foreground);
}

.login-reset-linked-phone__error {
  margin-top: 2px;
}

.login-reset-feedback {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  font-weight: 500;
}

.login-reset-feedback__icon {
  flex-shrink: 0;
  color: currentColor;
}

.login-reset-feedback__text {
  min-width: 0;
}

.login-reset-feedback--sending {
  color: var(--info);
}

.login-reset-feedback--sent {
  color: var(--success);
}

.login-field input {
  height: 40px;
  width: 100%;
  border-radius: 10px;
  border: 1px solid color-mix(in oklch, var(--border) 82%, transparent);
  background: var(--background);
  color: var(--foreground);
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease;
}

.login-field input::placeholder {
  color: var(--muted-foreground);
}

.login-field input:focus-visible {
  border-color: color-mix(in oklch, var(--ring) 42%, var(--border));
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--ring) 20%, transparent);
}

.login-field input.is-invalid {
  border-color: color-mix(in oklch, var(--destructive) 70%, var(--border));
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--destructive) 16%, transparent);
}

.login-field__input-wrap {
  position: relative;
}

.login-field__input-wrap--phone input {
  padding-left: 40px;
  padding-right: 12px;
}

.login-field__leading-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  width: 20px;
  height: 20px;
  transform: translateY(-50%);
  pointer-events: none;
  flex-shrink: 0;
}

.login-field__hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.3;
}

.login-field__hint--error {
  color: var(--destructive);
}

.login-email-start {
  display: grid;
  gap: 12px;
  width: 100%;
}

.login-email-start .login-field {
  width: 100%;
  max-width: 100%;
}

.login-email-start__continue {
  width: 100%;
  min-height: 46px;
  border: 0;
  border-radius: 10px;
  background: #000;
  color: #fff;
  font-size: 14px;
  line-height: 1;
  font-weight: 600;
  cursor: pointer;
  transition:
    opacity 120ms ease,
    transform 120ms ease,
    background-color 160ms ease,
    color 160ms ease;
}

.login-email-start__continue--loading:disabled,
.login-email-start__continue--success:disabled {
  opacity: 1;
  cursor: default;
}

.login-email-start__continue--success:disabled {
  background: var(--success);
  color: #0a0a0a;
}

.login-email-start__continue-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.login-email-start__electric {
  position: relative;
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  margin-right: -2px;
}

.login-email-start__electric-bolt {
  position: relative;
  z-index: 2;
  color: var(--color-brand-primary);
}

.login-email-start__electric-bolt.login-email-start__electric-bolt--on-dark {
  color: #fff;
}

.login-email-start__electric-bolt.login-email-start__electric-bolt--success-mark {
  color: #0a0a0a;
}

.login-email-start__continue--success:disabled .login-email-start__continue-content {
  color: #0a0a0a;
}

.login-email-start__continue--success:disabled :deep(svg.ui-icon) {
  color: #0a0a0a;
  fill: #0a0a0a;
}

.login-email-start__continue--success:disabled :deep(svg.ui-icon path) {
  fill: #0a0a0a;
}

.login-email-start__electric-halo {
  position: absolute;
  inset: 1px;
  border-radius: 999px;
  background: radial-gradient(circle, color-mix(in oklab, var(--color-brand-primary) 42%, white) 0%, transparent 68%);
  opacity: 0.2;
  transform: translateY(1px) scale(0.9);
}

.login-email-start__electric-spark {
  position: absolute;
  width: 8px;
  height: 11px;
  clip-path: polygon(38% 0%, 82% 0%, 57% 35%, 100% 35%, 30% 100%, 46% 58%, 8% 58%);
  box-shadow: 0 0 10px var(--cg-spark-color, #ffcc00);
  opacity: 0;
}

.login-email-start__electric-spark-a {
  --cg-spark-color: #ffcc00;
  top: 1px;
  right: -1px;
  transform-origin: 40% 55%;
  background: var(--cg-spark-color);
}

.login-email-start__electric-spark-b {
  --cg-spark-color: #ff453a;
  bottom: 1px;
  left: -2px;
  transform-origin: 52% 48%;
  background: var(--cg-spark-color);
}

.login-email-start__electric-spark-c {
  --cg-spark-color: #ff453a;
  top: -1px;
  left: 3px;
  transform-origin: 50% 50%;
  background: var(--cg-spark-color);
}

.login-email-start__electric-spark-d {
  --cg-spark-color: #ffcc00;
  bottom: -1px;
  right: 2px;
  transform-origin: 50% 50%;
  background: var(--cg-spark-color);
}

.login-email-start__continue:hover:not(:disabled) {
  opacity: 0.95;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-halo {
  animation: login-chip-electric-halo 1.3s ease-in-out infinite;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-spark-a {
  animation: login-chip-electric-spark-a 1.05s steps(5, end) infinite;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-spark-b {
  animation: login-chip-electric-spark-b 1.05s steps(5, end) infinite;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-spark-c {
  animation: login-chip-electric-spark-c 1.05s steps(5, end) infinite;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-spark-d {
  animation: login-chip-electric-spark-d 1.05s steps(5, end) infinite;
}

.login-email-start__continue:active:not(:disabled) {
  transform: translateY(1px);
}

.login-email-start__continue:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.is-spin {
  animation: login-spin 900ms linear infinite;
}

@keyframes login-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes login-chip-electric-halo {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.86);
  }
  40% {
    opacity: 0.7;
    transform: scale(1.08);
  }
  65% {
    opacity: 0.45;
    transform: scale(0.95);
  }
}

@keyframes login-chip-electric-spark-a {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(8deg) scale(0.6);
  }
  8% {
    opacity: 1;
    transform: translate(1px, -1px) rotate(16deg) scale(1);
  }
  14% {
    opacity: 0;
    transform: translate(4px, -3px) rotate(22deg) scale(0.8);
  }
  24% {
    opacity: 0.95;
    transform: translate(2px, -2px) rotate(14deg) scale(0.96);
  }
  31% {
    opacity: 0;
    transform: translate(6px, -4px) rotate(23deg) scale(0.7);
  }
}

@keyframes login-chip-electric-spark-b {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }
  42% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }
  52% {
    opacity: 1;
    transform: translate(-1px, 1px) rotate(-22deg) scale(1);
  }
  58% {
    opacity: 0;
    transform: translate(-4px, 3px) rotate(-26deg) scale(0.8);
  }
  68% {
    opacity: 0.9;
    transform: translate(-2px, 2px) rotate(-20deg) scale(0.94);
  }
  75% {
    opacity: 0;
    transform: translate(-6px, 5px) rotate(-28deg) scale(0.72);
  }
}

@keyframes login-chip-electric-spark-c {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }
  30% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }
  36% {
    opacity: 0.95;
    transform: translate(1px, -2px) rotate(10deg) scale(0.98);
  }
  43% {
    opacity: 0;
    transform: translate(3px, -5px) rotate(16deg) scale(0.72);
  }
}

@keyframes login-chip-electric-spark-d {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }
  60% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }
  67% {
    opacity: 0.95;
    transform: translate(1px, 2px) rotate(-11deg) scale(0.98);
  }
  74% {
    opacity: 0;
    transform: translate(3px, 5px) rotate(-18deg) scale(0.72);
  }
}
</style>
